import { Anchor } from '../Anchor';
import useTranslation from '../../context/Translation/useTranslation';
import { classNames } from '../../utils/classNames';
import Image from '../Image';

export interface ColorOption {
  label: string;
  imageUrl?: string;
  swatchColor?: string;
  isAvailable?: boolean;
  href?: string;
}

interface ItemProps {
  color: ColorOption;
  dark: boolean;
  selected?: boolean;
}

const Item = ({ color, dark, selected = false }: ItemProps) => {
  return (
    <p className="flex items-center" aria-current={selected}>
      {(color.swatchColor || color.imageUrl) && (
        <span
          className={classNames(
            'flex justify-center items-center shrink-0 mr-2 w-7 h-7 rounded-full bg-[#f6f4f3] border-2',
            selected ? 'border-brand-300' : 'border-warm-400',
          )}
          style={color.swatchColor ? { backgroundColor: color.swatchColor } : undefined}
        >
          {color.imageUrl && (
            <Image src={color.imageUrl} width={20} height={20} alt="" className="rounded-full" />
          )}
        </span>
      )}
      <span
        className={classNames(
          'tra-text-web-utl-link cursor-pointer hover:text-brand-300',
          dark && color.isAvailable !== false && 'text-white',
          dark && color.isAvailable === false && !selected && 'text-neutral-200',
          !dark && color.isAvailable === false && !selected && 'text-neutral-400',
          selected && 'font-semibold text-brand-300',
        )}
      >
        {color.label}
      </span>
    </p>
  );
};

export interface AlternateColorsProps {
  activeColor: string;
  colors: Array<ColorOption>;
  dark?: boolean;
  step?: number | `${number}`;
}

const AlternateColors = ({ activeColor, colors, dark = false, step }: AlternateColorsProps) => {
  const { t } = useTranslation();
  return (
    <div className={classNames(dark && 'text-white')}>
      <p className={classNames('tra-text-web-utl-link', dark && 'text-white')}>
        {step && `0${step} - `}
        {t('productDetailPageContent.selectColorText', undefined, {
          default: 'Select A Color',
        })}
        :
      </p>
      <p className="sr-only">
        {colors?.length}{' '}
        {t('productDetailPageContent.colorsAvailableText', undefined, {
          default: 'colors available',
        })}
        ,{' '}
        {t('productDetailPageContent.selectedOptionText', undefined, {
          default: 'Selected option is',
        })}{' '}
        {activeColor}
      </p>
      <div className="pt-2 flex items-start gap-6">
        {colors?.map((color) => {
          if (color.label !== activeColor) {
            return (
              <Anchor key={color.label} link={color.href}>
                <Item color={color} dark={dark} />
              </Anchor>
            );
          }
          return <Item key={color.label} selected color={color} dark={dark} />;
        })}
      </div>
    </div>
  );
};

export default AlternateColors;
